import React from "react";
import { Helmet } from "react-helmet-async";

import {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_META,
  stripLocalePrefix,
  type AppLocale,
} from "../i18n/locales";
import { useAppLocale } from "../i18n/LocaleRouting";
import {
  SITE_LOGO_URL,
  SITE_NAME,
  absoluteLocalizedUrl,
  absoluteUrl,
  alternateOgLocales,
} from "../i18n/seoUrls";

interface SeoHeadProps {
  title: string;
  description: string;
  /** Bare site path (no locale prefix), e.g. `/` or `/news`. */
  path?: string;
  imageUrl?: string;
  noindex?: boolean;
  keywords?: string;
  /** Override; defaults from current URL locale. */
  locale?: string;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
}

export default function SeoHead({
  title,
  description,
  path = "/",
  imageUrl = SITE_LOGO_URL,
  noindex = false,
  keywords,
  locale,
  structuredData,
}: SeoHeadProps) {
  const appLocale = useAppLocale();
  const barePath = stripLocalePrefix(path) || "/";
  const canonicalUrl = absoluteLocalizedUrl(barePath, appLocale);
  const robots = noindex ? "noindex,nofollow" : "index,follow";
  const fullTitle = `${title} | ${SITE_NAME}`;
  const ogLocale =
    locale ?? LOCALE_META[appLocale]?.ogLocale ?? LOCALE_META.en.ogLocale;
  const structuredDataItems = Array.isArray(structuredData)
    ? structuredData
    : structuredData
      ? [structuredData]
      : [];

  return (
    <Helmet>
      <html lang={LOCALE_META[appLocale].htmlLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={`${robots},max-image-preview:large`} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={canonicalUrl} />
      {LOCALES.map((loc: AppLocale) => (
        <link
          key={loc}
          rel="alternate"
          hrefLang={loc}
          href={absoluteLocalizedUrl(barePath, loc)}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={absoluteLocalizedUrl(barePath, DEFAULT_LOCALE)}
      />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      {alternateOgLocales(appLocale).map((alt) => (
        <meta key={alt} property="og:locale:alternate" content={alt} />
      ))}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta
        property="og:image"
        content={
          imageUrl.startsWith("http") ? imageUrl : absoluteUrl(imageUrl)
        }
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta
        name="twitter:image"
        content={
          imageUrl.startsWith("http") ? imageUrl : absoluteUrl(imageUrl)
        }
      />
      {structuredDataItems.map((item, index) => (
        <script key={`seo-jsonld-${index}`} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}
